// Presupuesto de traslado sanitario (servicios AZUL, destinatarios 4 y 6).
// Se abre desde la Consulta del servicio (Despachos y Finalizados): precarga
// los datos que tiene el servicio, el operador completa el resto y se genera
// el PDF con el diseño de Master Red, que se abre en una pestaña nueva.
import React, { Fragment, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Grid from "@mui/material/Grid";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import CreditCardIcon from "@mui/icons-material/CreditCard";
import generatePDF, { Resolution, Margin } from "react-to-pdf";

import logoMastered from "../../../assets/images/masteredlogo.png";
import imgFoto from "../../../assets/images/presupuesto/foto.png";
import imgFirma from "../../../assets/images/presupuesto/firma.png";
import imgCurva from "../../../assets/images/presupuesto/curva.png";
import imgAsterisco from "../../../assets/images/presupuesto/asterisco.png";

const MESES = [
  "Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio",
  "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre",
];

// ---- Importe en letras (pesos, sin centavos) ----
const UNIDADES = ["", "uno", "dos", "tres", "cuatro", "cinco", "seis", "siete", "ocho", "nueve"];
const ESPECIALES = {
  10: "diez", 11: "once", 12: "doce", 13: "trece", 14: "catorce", 15: "quince",
  16: "dieciséis", 17: "diecisiete", 18: "dieciocho", 19: "diecinueve",
  20: "veinte", 21: "veintiuno", 22: "veintidós", 23: "veintitrés", 24: "veinticuatro",
  25: "veinticinco", 26: "veintiséis", 27: "veintisiete", 28: "veintiocho", 29: "veintinueve",
};
const DECENAS = ["", "", "", "treinta", "cuarenta", "cincuenta", "sesenta", "setenta", "ochenta", "noventa"];
const CENTENAS = [
  "", "ciento", "doscientos", "trescientos", "cuatrocientos", "quinientos",
  "seiscientos", "setecientos", "ochocientos", "novecientos",
];

function menosDeMil(n) {
  if (n === 0) return "";
  if (n === 100) return "cien";
  const c = Math.floor(n / 100);
  const r = n % 100;
  let texto = CENTENAS[c];
  let resto = "";
  if (r > 0) {
    if (r < 10) resto = UNIDADES[r];
    else if (r < 30) resto = ESPECIALES[r];
    else {
      const d = Math.floor(r / 10);
      const u = r % 10;
      resto = DECENAS[d] + (u ? " y " + UNIDADES[u] : "");
    }
  }
  return [texto, resto].filter(Boolean).join(" ");
}

export function numeroALetras(numero) {
  const n = Math.floor(Math.abs(Number(numero) || 0));
  if (n === 0) return "cero";
  const millones = Math.floor(n / 1000000);
  const miles = Math.floor((n % 1000000) / 1000);
  const resto = n % 1000;
  const partes = [];
  if (millones > 0) {
    partes.push(
      millones === 1
        ? "un millón"
        : menosDeMil(millones).replace(/veintiuno$/, "veintiún").replace(/uno$/, "un") + " millones",
    );
  }
  if (miles > 0) {
    partes.push(miles === 1 ? "mil" : menosDeMil(miles).replace(/veintiuno$/, "veintiún").replace(/uno$/, "un") + " mil");
  }
  if (resto > 0) partes.push(menosDeMil(resto));
  return partes.join(" ");
}

// ---- Datos iniciales tomados del servicio ----
function datosDesdeServicio(servicio) {
  const hoy = new Date();
  const traslado = servicio && servicio.traslados ? servicio.traslados : null;
  const fechaProgramada =
    servicio && servicio.llegadaProgramada
      ? String(servicio.llegadaProgramada).substring(0, 5).replace("/", "-")
      : "";
  return {
    senores: (servicio && (servicio.nombreTitular || servicio.obraSocial)) || "",
    atencion: "A quien corresponda",
    lugar: "Buenos Aires,",
    fecha: `${MESES[hoy.getMonth()]} de ${hoy.getFullYear()}`,
    paciente: (servicio && servicio.nombre) || "",
    edad: servicio && servicio.edad ? `${servicio.edad} años` : "",
    dni: (servicio && servicio.numeroDocumento) || "",
    tipoUnidad:
      traslado && traslado.tiposMoviles && traslado.tiposMoviles.nombre
        ? traslado.tiposMoviles.nombre
        : "",
    origen: (servicio && servicio.domicilio) || "",
    destino: (traslado && traslado.destino) || "",
    motivo: (servicio && servicio.motivoConsulta) || "",
    fechaTraslado: fechaProgramada,
    valor: "",
    espera: "1 hora",
    formaPago: "Al contado.",
  };
}

// Colores del diseño
const AZUL = "#14233c";
const NARANJA = "#f47920";
const GRIS = "#5b6573";

export default function PresupuestoTraslado({ open, onClose, servicio }) {
  const [datos, setDatos] = useState(datosDesdeServicio(servicio));
  const [generando, setGenerando] = useState(false);

  // PDF generado (URL del blob) que se muestra en el visor
  const [pdfUrl, setPdfUrl] = useState(null);
  const [errorPdf, setErrorPdf] = useState(null);

  const confirmarImporte = () => {
    if (!valorNumero || generando) return;
    generar();
  };

  const cerrarVisor = () => {
    if (pdfUrl) URL.revokeObjectURL(pdfUrl);
    setPdfUrl(null);
  };
  const hojaRef = useRef(null);

  // Cada vez que se abre, se vuelven a cargar los datos del servicio
  useEffect(() => {
    if (open) setDatos(datosDesdeServicio(servicio));
  }, [open, servicio]);

  const cambiar = (campo) => (e) => setDatos({ ...datos, [campo]: e.target.value });

  const valorNumero = Number(String(datos.valor).replace(/\D/g, "")) || 0;
  const valorTexto = valorNumero
    ? `$ ${valorNumero.toLocaleString("es-AR")}.-`
    : "$ -";

  // Arma el PDF a partir de la hoja y lo muestra en el visor
  const generar = async () => {
    setGenerando(true);
    setErrorPdf(null);
    try {
      const pdf = await generatePDF(() => hojaRef.current, {
        method: "build",
        filename: `presupuesto_${(servicio && servicio.id) || "traslado"}.pdf`,
        // MEDIUM (x3) ~ 290 dpi: alcanza para imprimir y es mucho más liviano que HIGH (x7)
        resolution: Resolution.MEDIUM,
        page: { format: "A4", orientation: "portrait", margin: Margin.NONE },
        canvas: { mimeType: "image/jpeg", qualityRatio: 0.95 },
        overrides: { pdf: { compress: true } },
      });
      const blob = pdf.output("blob");
      setPdfUrl(URL.createObjectURL(blob));
    } catch (error) {
      console.log("Error al generar el presupuesto: ", error);
      setErrorPdf(String((error && error.message) || error));
    } finally {
      setGenerando(false);
    }
  };

  const filasTabla = [
    ["PACIENTE", datos.paciente],
    ["EDAD", datos.edad],
    ["DNI", datos.dni],
    ["TIPO DE UNIDAD", datos.tipoUnidad],
    ["ORIGEN", datos.origen],
    ["DESTINO", datos.destino],
    ["MOTIVO DE TRASLADO", datos.motivo],
    ["FECHA DE TRASLADO", datos.fechaTraslado],
  ];

  const campo = (label, nombre, md = 6, extra = {}) => (
    <Grid item xs={12} md={md}>
      <TextField
        label={label}
        value={datos[nombre]}
        onChange={cambiar(nombre)}
        fullWidth
        size="small"
        InputLabelProps={{ shrink: true }}
        {...extra}
      />
    </Grid>
  );

  return (
    <Fragment>
      {/* zIndex: por encima del dialog de la Consulta (PrimeReact) */}
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth sx={{ zIndex: 3000 }}>
        <DialogTitle sx={{ fontWeight: 700 }}>Presupuesto de traslado sanitario</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" sx={{ mb: 2, color: "text.secondary" }}>
            Los datos que tiene el servicio ya vienen cargados: revisalos, corregí lo
            que haga falta y cargá el importe del traslado.
          </Typography>
          <Grid container spacing={2}>
            {campo("Señores", "senores", 6)}
            {campo("Atención", "atencion", 6)}
            {campo("Lugar", "lugar", 4)}
            {campo("Fecha del presupuesto", "fecha", 8)}
            {campo("Paciente", "paciente", 6)}
            {campo("Edad", "edad", 3)}
            {campo("DNI", "dni", 3)}
            {campo("Tipo de unidad", "tipoUnidad", 4)}
            {campo("Motivo de traslado", "motivo", 5)}
            {campo("Fecha de traslado", "fechaTraslado", 3)}
            {campo("Origen", "origen", 12)}
            {campo("Destino", "destino", 12)}
            {campo("Espera incluida", "espera", 6)}
            {campo("Forma de pago", "formaPago", 6)}
            <Grid item xs={12}>
              <TextField
                label="Importe del traslado ($)"
                value={datos.valor}
                onChange={cambiar("valor")}
                onKeyDown={(e) => {
                  if (e.key === "Enter") confirmarImporte();
                }}
                autoFocus
                required
                fullWidth
                inputProps={{ inputMode: "numeric", style: { fontSize: 20, fontWeight: 700 } }}
                InputLabelProps={{ shrink: true }}
                helperText={
                  valorNumero
                    ? `$ ${valorNumero.toLocaleString("es-AR")}.- (pesos ${numeroALetras(valorNumero)})`
                    : "Ingrese el importe para poder generar el presupuesto"
                }
                sx={{ "& .MuiOutlinedInput-root": { bgcolor: "#fbf5f0" } }}
              />
            </Grid>
          </Grid>
          {errorPdf ? (
            <Typography variant="body2" color="error" sx={{ mt: 2 }}>
              {`No se pudo generar el PDF: ${errorPdf}`}
            </Typography>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button color="inherit" onClick={onClose}>
            Cancelar
          </Button>
          <Button
            variant="contained"
            onClick={confirmarImporte}
            disabled={generando || !valorNumero}
          >
            {generando ? "Generando..." : "Generar presupuesto"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Visor: el PDF se ve en pantalla; desde la barra del visor se puede
          descargar o imprimir */}
      <Dialog
        open={!!pdfUrl}
        onClose={cerrarVisor}
        maxWidth="lg"
        fullWidth
        sx={{ zIndex: 3001 }}
        PaperProps={{ sx: { height: "92vh" } }}
      >
        <DialogTitle sx={{ fontWeight: 700 }}>Presupuesto de traslado sanitario</DialogTitle>
        <DialogContent dividers sx={{ p: 0 }}>
          {pdfUrl ? (
            <iframe
              title="Presupuesto"
              src={pdfUrl}
              style={{ width: "100%", height: "100%", border: 0 }}
            />
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button
            component="a"
            href={pdfUrl || undefined}
            download={`presupuesto_${(servicio && servicio.id) || "traslado"}.pdf`}
          >
            Descargar
          </Button>
          <Button variant="contained" onClick={cerrarVisor}>
            Cerrar
          </Button>
        </DialogActions>
      </Dialog>


      {/* Hoja del presupuesto (A4 a 96 dpi, ~793 x 1120 px). Se dibuja fuera de
          la pantalla y se convierte a PDF con react-to-pdf. */}
      {/* Portal al body: así ningún dialog que la contenga la recorta */}
      {open
        ? createPortal(
        <div style={{ position: "fixed", left: -10000, top: 0 }}>
          <div
            ref={hojaRef}
            style={{
              position: "relative",
              // Apenas menor que A4 a 96 dpi (793,7 x 1122,5): si se pasa,
              // react-to-pdf arma una 2da página vacía y falla ("UNKNOWN")
              width: 793,
              height: 1120,
              background: "#ffffff",
              overflow: "hidden",
              fontFamily: "'Segoe UI', Arial, sans-serif",
              color: AZUL,
            }}
          >
            {/* Foto y decoraciones */}
            <img src={imgFoto} alt="" style={{ position: "absolute", right: 0, top: 0, width: 395 }} />
            <img src={imgCurva} alt="" style={{ position: "absolute", left: 0, bottom: 0, width: 271 }} />
            <img src={imgAsterisco} alt="" style={{ position: "absolute", right: 0, bottom: 0, width: 112 }} />

            {/* Logo y título */}
            <img src={logoMastered} alt="Master Red" style={{ position: "absolute", left: 58, top: 30, width: 185 }} />
            <div style={{ position: "absolute", left: 49, top: 166, width: 43, height: 3, background: NARANJA }} />
            <div style={{ position: "absolute", left: 49, top: 188, fontSize: 12, letterSpacing: 7, color: GRIS }}>
              PRESUPUESTO
            </div>
            <div style={{ position: "absolute", left: 47, top: 208, fontSize: 50, fontWeight: 800, lineHeight: 1 }}>
              TRASLADO
            </div>
            <div style={{ position: "absolute", left: 47, top: 258, fontSize: 50, fontWeight: 300, lineHeight: 1 }}>
              SANITARIO
            </div>

            {/* Destinatario y fecha */}
            <div style={{ position: "absolute", left: 52, top: 338, width: 380, fontSize: 14, lineHeight: 1.35 }}>
              <div>Señores:</div>
              <div style={{ fontWeight: 700, fontSize: 15 }}>{datos.senores}</div>
              <div style={{ marginTop: 14 }}>Atención:</div>
              <div>{datos.atencion}</div>
            </div>
            <div style={{ position: "absolute", left: 447, top: 337, width: 1, height: 87, background: "#c9cfd8" }} />
            <div
              style={{
                position: "absolute", left: 476, top: 348, width: 42, height: 42, borderRadius: 21,
                background: "#eef1f5", display: "flex", alignItems: "center", justifyContent: "center",
              }}
            >
              <CalendarMonthOutlinedIcon style={{ fontSize: 22, color: AZUL }} />
            </div>
            <div style={{ position: "absolute", left: 534, top: 350, fontSize: 14, lineHeight: 1.4 }}>
              <div>{datos.lugar}</div>
              <div>{datos.fecha}</div>
            </div>

            {/* Introducción */}
            <div style={{ position: "absolute", left: 52, top: 456, fontSize: 13, lineHeight: 1.55 }}>
              <div>De nuestra mayor consideración:</div>
              <div>De acuerdo a lo solicitado, tenemos el agrado de cotizar el siguiente traslado.</div>
            </div>

            {/* Datos del traslado */}
            <div
              style={{
                position: "absolute", left: 41, top: 511, width: 712, borderRadius: 8,
                background: "#f5f7fa", padding: "4px 14px",
              }}
            >
              {filasTabla.map(([etiqueta, valor], i) => (
                <div
                  key={etiqueta}
                  style={{
                    display: "flex", alignItems: "center", minHeight: 32,
                    borderBottom: i < filasTabla.length - 1 ? "1px solid #e1e5eb" : "none",
                  }}
                >
                  <div style={{ width: 225, paddingLeft: 14, fontSize: 10, letterSpacing: 3.2, color: GRIS }}>
                    {etiqueta}
                  </div>
                  <div style={{ flex: 1, fontSize: 15, fontWeight: 600 }}>{valor}</div>
                </div>
              ))}
            </div>

            {/* Valor del traslado */}
            <div
              style={{
                position: "absolute", left: 41, top: 787, width: 712, height: 128,
                borderRadius: 8, background: "#fbf5f0",
              }}
            >
              <div style={{ position: "absolute", left: 27, top: 16, fontSize: 10, letterSpacing: 3.2, color: AZUL }}>
                VALOR DEL TRASLADO
              </div>
              <div style={{ position: "absolute", left: 27, top: 32, width: 32, height: 2, background: NARANJA }} />
              <div style={{ position: "absolute", left: 25, top: 36, fontSize: 44, fontWeight: 800, lineHeight: 1.1 }}>
                {valorTexto}
              </div>
              <div style={{ position: "absolute", left: 27, top: 87, fontSize: 13, lineHeight: 1.3 }}>
                {valorNumero ? `(pesos ${numeroALetras(valorNumero)})` : ""}
              </div>
              <div style={{ position: "absolute", left: 27, top: 104, fontSize: 11.5, color: GRIS }}>
                + IVA, en caso que corresponda.
              </div>
              <div style={{ position: "absolute", left: 429, top: 26, width: 1, height: 76, background: "#d9d2cb" }} />
              <AccessTimeIcon style={{ position: "absolute", left: 482, top: 34, fontSize: 52, color: NARANJA }} />
              <div style={{ position: "absolute", left: 553, top: 34, width: 150, fontSize: 13, lineHeight: 1.45 }}>
                La presente cotización contempla <b>{datos.espera}</b> <b>de espera</b> concretada.
              </div>
            </div>

            {/* Forma de pago */}
            <div
              style={{
                position: "absolute", left: 41, top: 925, width: 712, height: 60,
                borderRadius: 8, background: "#f5f7fa",
              }}
            >
              <CreditCardIcon style={{ position: "absolute", left: 22, top: 14, fontSize: 32, color: AZUL }} />
              <div style={{ position: "absolute", left: 96, top: 12, fontSize: 10, letterSpacing: 3.2 }}>
                FORMA DE PAGO
              </div>
              <div style={{ position: "absolute", left: 96, top: 27, width: 32, height: 2, background: NARANJA }} />
              <div style={{ position: "absolute", left: 96, top: 32, fontSize: 14, fontWeight: 600 }}>
                {datos.formaPago}
              </div>
            </div>

            {/* Cierre y firma */}
            <div style={{ position: "absolute", left: 134, top: 1009, fontSize: 12.5, lineHeight: 1.55 }}>
              <div>Quedamos a su disposición ante cualquier consulta.</div>
              <div>Saluda atentamente,</div>
            </div>
            <img src={imgFirma} alt="" style={{ position: "absolute", left: 556, top: 956, width: 113 }} />
            <div style={{ position: "absolute", left: 537, top: 1062, width: 137, height: 2, background: NARANJA }} />
            <div style={{ position: "absolute", left: 517, top: 1069, width: 177, textAlign: "center" }}>
              <div style={{ fontSize: 13, fontWeight: 700 }}>Fabián Bertolucci</div>
              <div style={{ fontSize: 12, color: GRIS }}>Ejecutivo de cuentas</div>
            </div>
          </div>
        </div>,
            document.body,
          )
        : null}
    </Fragment>
  );
}
